import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { ViewState } from 'react-map-gl/mapbox'

import {
  IAddressBounds,
  IDeliveryConfigurations,
  IGeoJson2DGeographicCoordinates,
  IGeoJson2DGeographicCoordinatesInput,
  IGlobesSetupSettings,
  IGlobeViewMapFile,
  IGlobeViewSpecificItemData,
  IGlobeViewStyle,
  IGlobeViewStyleInput,
  ILatLng,
  ILogisticsConfigurations,
  IOrderedSitemap,
  IWorkflowConfigurations,
  MapFileType,
} from '~/client/graph'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import { DEFAULT_GLOBE_VIEW_STYLE } from '../components/MapBoxViewer/mapboxConstants'
import MapViewItemBase from '../components/SitemapHelpers/models/MapViewItemBase'
import GlobeView from '../models/GlobeView'
import IGeoPosition from '../models/IGeoPosition'
import LocationBase from '../models/LocationObjects/LocationBase'
import GlobeViewsStore from './domain/GlobeViews.store'
import LocationAttributesStore from './domain/LocationAttributes.store'
import SitemapItemsStore from './domain/SitemapItems.store'
import TagsStore from './domain/Tags.store'
import UserProjectsStore from './domain/UserProjects.store'
import EventsStore from './EventStore/Events.store'
import EventTypes from './EventStore/eventTypes'
import GlobeViewControlStore, { DEFAULT_NAME } from './GlobeViewControl.store'

export default class GlobeViewSetupStore extends GlobeViewControlStore {
  @observable public editableGlobeName: string = ''
  @observable public deletableGlobe: GlobeView = null
  @observable public isAssignGlobeDialogShown: boolean = false
  // Element the location-association popover anchors to: the right panel's
  // LBS Tag field; the sidebar flow uses its own anchor in `GeneralMapViewSetUp`.
  @observable.ref public assignDialogAnchor: HTMLElement | null = null

  public constructor(
    eventsStore: EventsStore,
    globeViewsStore: GlobeViewsStore,
    private readonly userProjectsStore: UserProjectsStore,
    sitemapItemsStore: SitemapItemsStore,
    locationAttributesStore: LocationAttributesStore,
    private readonly tagsStore: TagsStore,
  ) {
    super(
      eventsStore,
      globeViewsStore,
      sitemapItemsStore,
      locationAttributesStore,
    )
    makeObservable(this)
  }

  public selectInitialGlobe = (globe?: GlobeView): void => {
    this.selectGlobe(
      globe || this.lastEditedGlobe || this.globeViewsStore.list[0],
    )
  }

  @action.bound
  public toggleAssignGlobeDialog(): void {
    this.isAssignGlobeDialogShown = !this.isAssignGlobeDialogShown
  }

  @action.bound
  public setAssignDialogAnchor(anchor: HTMLElement | null): void {
    this.assignDialogAnchor = anchor
  }

  @action.bound
  public showAssignGlobeDialog(): void {
    this.isAssignGlobeDialogShown = true
  }

  @action.bound
  public hideAssignGlobeDialog(): void {
    this.isAssignGlobeDialogShown = false
  }

  public selectDeletableGlobe = (globe: GlobeView): void => {
    if (this.globeViewsStore.list.length <= 1) {
      throw Error('Last globe can not be deleted')
    }
    this.deletableGlobe = globe
  }

  public copyGlobe = async (globe: GlobeView): Promise<void> => {
    const copy = globe.getFullCopy()
    copy.id = null

    const id = await this.saveGlobeView(copy)
    if (id) {
      this.selectGlobe(this.globeViewsStore.adoptSaved(copy, id))
    }
  }

  public deselectDeletableGlobe = (): void => {
    this.deletableGlobe = null
  }

  public saveGlobeViewName = async (): Promise<string> => {
    if (
      !this.editableGlobeName ||
      this.selectedGlobeView.isNameEqual(this.editableGlobeName)
    ) {
      return
    }

    const globeToSave = this.selectedGlobeView.getCopy()
    globeToSave.name = this.editableGlobeName
    return new Promise<string>(resolve => {
      this.globeViewsStore.save(
        globeToSave,
        id => {
          this.saveNewCallback(id)
          resolve(id)
        },
        true,
      )
    })
  }

  @action.bound
  public saveGlobeViewStyle(style: IGlobeViewStyleInput) {
    this.globeViewsStore.saveGlobeViewStyle(style, this.selectedGlobeViewId)
  }

  public async selectGlobe(globe: GlobeView): Promise<void> {
    this.deselectGlobe()
    if (!globe) {
      return
    }

    this.selectedGlobeViewId = globe.id
    this.editableGlobeName = globe.name || DEFAULT_NAME
    this.setLastEditedGlobeView(globe.id)
  }

  @computed
  private get allLocations() {
    return [
      ...this.locationAttributesStore.buildingsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.zonesStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.gatesStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.routesStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.offloadingEquipmentsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.levelsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.areasStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.stagingsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.interiorDoorsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      ...this.locationAttributesStore.interiorPathsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
      // The catch-all: a location of a project-added kind, or re-kinded into one (STX1-597)
      ...this.locationAttributesStore.otherLocationsStore.list.filter(
        b => b.assignedGlobes?.length,
      ),
    ]
  }

  @computed
  public get globeToLocation(): {
    [globeId: string]: LocationBase
  } {
    const map = {}

    this.allGlobeViews.forEach(globe => {
      const location = this.allLocations.find(l => l.isGlobeAssigned(globe.id))

      const chains = location?.getHierarchyChainObjs(
        this.tagsStore.tagStoreByTagTypeMap,
      )

      map[globe.id] = location || null

      if (chains?.length) {
        chains.forEach(chain => {
          map[globe.id] = chain || null
        })
      }
    })

    return map
  }

  @computed
  public get deliveriesAssignedGlobes(): { [filterType: string]: boolean } {
    return this.appState.deliveriesMapIdsList
      .filter(m => m.globeViewId)
      .map(m => m.globeViewId)
      .reduce((map, globeViewId) => {
        map[globeViewId] = true
        return map
      }, {})
  }

  @computed
  public get siteAssignedGlobes(): { [filterType: string]: boolean } {
    return this.appState.logisticsMapIdsList
      .filter(m => m.globeViewId)
      .map(m => m.globeViewId)
      .reduce((map, globeViewId) => {
        map[globeViewId] = true
        return map
      }, {})
  }

  @computed
  public get workflowsAssignedGlobes(): { [filterType: string]: boolean } {
    return this.appState.workflowsMapIdsList
      .filter(m => m.globeViewId)
      .map(m => m.globeViewId)
      .reduce((map, globeViewId) => {
        map[globeViewId] = true
        return map
      }, {})
  }

  @action.bound
  public setLastEditedGlobeView(lastEditedGlobeId: string): void {
    const { userActiveProjectSettings } = this.appState
    const userActiveProjectSettingsDto = userActiveProjectSettings.toDto()

    const globeSettings: IGlobesSetupSettings = {
      lastEditedGlobeId,
    }

    userActiveProjectSettingsDto.globesSetupSettings = globeSettings

    this.userProjectsStore.save([userActiveProjectSettingsDto])
  }

  @action.bound
  public changeName(event: React.ChangeEvent<HTMLInputElement>): void {
    this.editableGlobeName = event.target.value?.trim()
  }

  @action.bound
  public changeOrientationLock(): void {
    this.selectedGlobeView.isOrientationLocked =
      !this.selectedGlobeView.isOrientationLocked
    this.saveGlobeView()
  }

  public deleteGlobe = (): void => {
    this.deselectGlobe()
    this.globeViewsStore.removeOne(this.deletableGlobe.id)
    this.deselectDeletableGlobe()
    this.selectGlobe(this.globeViewsStore.list[0])
  }

  public get lastEditedGlobe(): GlobeView {
    const { globesSetupSettings } = this.appState.userActiveProjectSettings

    const lastEditedGlobeId = globesSetupSettings?.lastEditedGlobeId

    if (!lastEditedGlobeId) {
      return null
    }

    if (this.globeViewsStore.byId.has(lastEditedGlobeId)) {
      return this.globeViewsStore.byId.get(lastEditedGlobeId)
    }
  }

  public updateGlobeView = async (
    geoposition?: IGeoPosition,
    bounds?: IGeoJson2DGeographicCoordinatesInput[],
    items?: IGlobeViewSpecificItemData[],
    mapFiles?: IGlobeViewMapFile[],
    style?: IGlobeViewStyle,
    filledImage?: string,
  ): Promise<void> => {
    const newGlobe = this.selectedGlobeView.getCopy()
    if (geoposition) {
      newGlobe.setGeoposition(geoposition, bounds)
    }

    newGlobe.name = this.editableGlobeName
    if (items) {
      newGlobe.items = items
    }
    if (mapFiles) {
      newGlobe.mapFiles = mapFiles
    }
    if (filledImage) {
      newGlobe.filledImage = filledImage
    }
    if (style) {
      newGlobe.style = style
    }

    this.saveGlobeView(newGlobe, filledImage)
  }

  public createNewPlaceholder = async (
    geoCorners: IGeoJson2DGeographicCoordinatesInput[],
    viewport: ViewState,
    bounds?: IAddressBounds,
    items?: IGlobeViewSpecificItemData[],
    mapFiles?: IGlobeViewMapFile[],
    style?: IGlobeViewStyle,
    filledImage?: string,
  ): Promise<string> => {
    const {
      projectAddress,
      activeProject: { id },
    } = this.appState
    const newGlobe = new GlobeView(null, DEFAULT_NAME, id)

    newGlobe.mapFiles = []
    newGlobe.items = []
    newGlobe.bounds = geoCorners?.length
      ? {
          ne: { lat: geoCorners[0].latitude, lng: geoCorners[0].longitude },
          sw: { lat: geoCorners[3].latitude, lng: geoCorners[3].longitude },
        }
      : bounds

    newGlobe.center = viewport
      ? {
          lat: viewport.latitude,
          lng: viewport.longitude,
        }
      : projectAddress.center

    if (filledImage) {
      newGlobe.filledImage = filledImage
    }
    newGlobe.altitude = 0
    newGlobe.bearing = viewport?.bearing || 0
    newGlobe.geoCorners = geoCorners
    newGlobe.pitch = viewport?.pitch || 0
    newGlobe.zoom = viewport?.zoom || projectAddress.zoom
    if (items) {
      newGlobe.items = items
    }

    if (mapFiles) {
      newGlobe.mapFiles = mapFiles
    }

    if (style) {
      newGlobe.style = style
    }

    runInAction(() => {
      this.editableGlobeName = DEFAULT_NAME
    })

    // Selection is left alone until the save returns: clearing it up front left the editor on no
    // view at all whenever the save's callback could not find the new globe yet.
    const savedId = await this.saveGlobeView(newGlobe)
    if (savedId) {
      this.selectGlobe(this.globeViewsStore.adoptSaved(newGlobe, savedId))
    }
    return savedId
  }

  @action.bound
  public onSiteGlobeSectionClick(globeId: string) {
    const { configurations } = this.appState.logistics
    if (this.siteAssignedGlobes[globeId]) {
      this.removeGlobeFromConfig(
        configurations,
        globeId,
        e.SAVE_LOGISTICS_CONFIGURATIONS,
      )
    } else {
      this.addGlobeToConfig(
        configurations.maps,
        this.appState.activeProject.id,
        globeId,
        e.SAVE_LOGISTICS_CONFIGURATIONS,
      )
    }
  }

  @action.bound
  public onWorkflowsGlobeSectionClick(globeId: string) {
    const { configurations } = this.appState.workflows
    if (this.workflowsAssignedGlobes[globeId]) {
      this.removeGlobeFromConfig(
        configurations,
        globeId,
        e.SAVE_WORKFLOWS_CONFIGURATIONS,
      )
    } else {
      this.addGlobeToConfig(
        configurations.maps,
        this.appState.activeProject.id,
        globeId,
        e.SAVE_WORKFLOWS_CONFIGURATIONS,
      )
    }
  }

  @action.bound
  public onDeliveryGlobeSectionClick(globeId: string) {
    const { configurations } = this.appState.delivery
    if (this.deliveriesAssignedGlobes[globeId]) {
      this.removeGlobeFromConfig(
        configurations,
        globeId,
        e.SAVE_DELIVERY_CONFIGURATIONS,
      )
    } else {
      this.addGlobeToConfig(
        configurations.maps,
        this.appState.activeProject.id,
        globeId,
        e.SAVE_DELIVERY_CONFIGURATIONS,
      )
    }
  }

  public updateGlobeName = (globe: GlobeView, name: string): void => {
    const newGlobe = globe.getCopy()
    newGlobe.name = name || this.editableGlobeName

    this.saveGlobeView(newGlobe)
  }

  private assignGlobeToProjectLocation = async (
    globeId: string,
  ): Promise<void> => {
    const newProjectLocation =
      this.locationAttributesStore.projectLocation.copy()
    newProjectLocation.assignedGlobes.push(globeId)
    await this.locationAttributesStore.projectLocationsStore.saveItem(
      newProjectLocation,
    )
  }

  public saveGlobeView = async (
    globeView?: GlobeView,
    filledImage?: string,
  ): Promise<string> => {
    if (this.isLoading) {
      return
    }
    const globeToSave = globeView || this.selectedGlobeView.getCopy()
    if (filledImage) {
      globeToSave.filledImage = filledImage
    }
    return new Promise<string>(resolve => {
      this.globeViewsStore.save(
        globeToSave,
        id => {
          this.saveNewCallback(id)
          if (
            !this.locationAttributesStore.allAttributes.some(a =>
              a.isGlobeAssigned(id),
            )
          ) {
            this.assignGlobeToProjectLocation(id)
          }
          resolve(id)
        },
        true,
      )
    })
  }

  @action.bound
  private removeGlobeFromConfig(
    config:
      | IDeliveryConfigurations
      | IWorkflowConfigurations
      | ILogisticsConfigurations,
    globeViewId: string,
    eventType: EventTypes,
  ) {
    if (!globeViewId) {
      return
    }

    config.maps = config.maps.filter(m => m.globeViewId !== globeViewId)

    this.eventsStore.dispatch(eventType, {
      maps: config.maps,
    })
  }

  private createNewGlobeConfig = (
    maps: IOrderedSitemap[],
    projectId: string,
    globeViewId: string,
    eventType: EventTypes,
  ) => {
    if (!globeViewId || maps.some(s => s.globeViewId === globeViewId)) {
      return
    }

    maps.push({
      globeViewId: globeViewId,
      order: maps.length,
    })

    this.eventsStore.dispatch(eventType, {
      id: null,
      maps,
      projectId,
    })
  }

  @action.bound
  private addGlobeToConfig(
    maps: IOrderedSitemap[],
    projectId: string,
    globeViewId: string,
    eventType: EventTypes,
  ) {
    if (!globeViewId) {
      return
    }
    if (!maps.some(s => s.globeViewId === globeViewId)) {
      maps.push({
        globeViewId: globeViewId,
        order: maps.length,
      })

      this.eventsStore.dispatch(eventType, {
        maps,
        projectId,
      })
    }
  }

  public createNewGlobeView = async (
    filledImage?: string,
    bounds?,
    center?: ILatLng,
    zoom?: number,
    altitude?: number,
    bearing?: number,
    pitch?: number,
    geoCorners?: IGeoJson2DGeographicCoordinates[],
    projectId?: string,
  ): Promise<string> => {
    const globe = new GlobeView(
      null,
      DEFAULT_NAME,
      projectId,
      [],
      [],
      null,
      null,
      bounds,
      center,
      zoom,
      altitude,
      pitch,
      bearing,
      geoCorners,
      true,
      filledImage,
      { ...DEFAULT_GLOBE_VIEW_STYLE },
    )

    return new Promise<string>(resolve => {
      this.globeViewsStore.save(
        globe,
        id => {
          this.saveNewCallback(id)
          resolve(id)
        },
        true,
      )
    })
  }

  public assignGlobeToAllApps = (globeId: string, projectId: string) => {
    this.createNewGlobeConfig(
      [],
      projectId,
      globeId,
      e.SAVE_WORKFLOWS_CONFIGURATIONS,
    )
    this.createNewGlobeConfig(
      [],
      projectId,
      globeId,
      e.SAVE_LOGISTICS_CONFIGURATIONS,
    )
    this.createNewGlobeConfig(
      [],
      projectId,
      globeId,
      e.SAVE_DELIVERY_CONFIGURATIONS,
    )
  }

  public deselectGlobe(): void {
    this.selectedGlobeViewId = null
    this.editableGlobeName = ''
  }

  public saveGlobeViewImage = async (
    filledImage: string,
    globeView?: GlobeView,
  ): Promise<string> => {
    const globeToSave = globeView || this.selectedGlobeView.getCopy()
    globeToSave.filledImage = filledImage
    return new Promise<string>(resolve => {
      this.globeViewsStore.save(
        globeToSave,
        id => {
          this.saveNewCallback(id)
          resolve(id)
        },
        true,
      )
    })
  }

  public addItemToGlobeView = async (
    sitemapItemId: string,
    sitemapItemBase?: MapViewItemBase,
  ): Promise<string | null> => {
    const itemWithIndex = this.selectedGlobeView.displayDataMap[sitemapItemId]
    if (sitemapItemBase) {
      const newItem = {
        ...sitemapItemBase.getDisplayData(true),
        sitemapItemId,
        id: itemWithIndex?.item?.id,
        isHidden: false,
        isReferenced: true,
        globeViewId: this.selectedGlobeView.id,
        projectId: this.selectedGlobeView.projectId,
      } as IGlobeViewSpecificItemData
      return await this.saveGlobeViewItems([newItem])
    } else {
      return await this.saveGlobeViewItems([
        {
          sitemapItemId,
          id: itemWithIndex?.item?.id,
          isHidden: false,
          globeViewId: this.selectedGlobeView.id,
          projectId: this.selectedGlobeView.projectId,
        },
      ])
    }
  }

  public setSitemapToGlobeView = async (sitemapId: string): Promise<string> => {
    const newGlobe = this.selectedGlobeView.getCopy()
    const isAdded = newGlobe.sitemapsMap[sitemapId]
    if (isAdded) {
      newGlobe.mapFiles = newGlobe.mapFiles.filter(
        f => f.entityType !== MapFileType.Sitemap || f.entityId !== sitemapId,
      )
    } else {
      newGlobe.mapFiles.push({
        entityType: MapFileType.Sitemap,
        entityId: sitemapId,
      })
    }

    return await this.saveGlobeView(newGlobe)
  }

  public setTilesetToGlobeView = async (tilesetId: string): Promise<string> => {
    const newGlobe = this.selectedGlobeView.getCopy()
    const isAdded = newGlobe.tilesetsMap[tilesetId]
    if (isAdded) {
      newGlobe.mapFiles = newGlobe.mapFiles.filter(
        f => f.entityType !== MapFileType.Tileset || f.entityId !== tilesetId,
      )
    } else {
      newGlobe.mapFiles.push({
        entityType: MapFileType.Tileset,
        entityId: tilesetId,
      })
    }

    return await this.saveGlobeView(newGlobe)
  }

  public setRemoteTilesetToGlobeView = async (remoteTilesetId: string) => {
    const newGlobe = this.selectedGlobeView.getCopy()
    const isAdded = newGlobe.remoteTilesetsMap[remoteTilesetId]
    if (isAdded) {
      newGlobe.mapFiles = newGlobe.mapFiles.filter(
        f =>
          f.entityType !== MapFileType.RemoteTileset ||
          f.entityId !== remoteTilesetId,
      )
    } else {
      newGlobe.mapFiles.push({
        entityType: MapFileType.RemoteTileset,
        entityId: remoteTilesetId,
      })
    }
    return await this.saveGlobeView(newGlobe)
  }

  public async updateItems(globeView: GlobeView): Promise<string> {
    return new Promise<string>(resolve => {
      this.globeViewsStore.save(globeView, id => {
        this.saveNewCallback(id)
        resolve(id)
      })
    })
  }

  // Selects by id, not by instance: this runs from the save's own callback, which fires before
  // the store has merged the new globe, so a lookup here misses and would deselect everything —
  // leaving the editor with no view, no image and nothing to end its loader.
  @action.bound
  public saveNewCallback(id: string): void {
    const globe = this.globeViewsStore.byId.get(id)
    if (globe) {
      this.selectGlobe(globe)
    }
    // A miss means the store has not merged the globe yet, which happens for a brand-new one:
    // the caller that holds the instance adopts it and selects it. Deselecting here instead would
    // leave the editor on no view at all.
  }

  public saveNewGLobeView = async (
    geoposition?: IGeoPosition,
    bounds?,
    filledImage?: string,
  ): Promise<void> => {
    const globeView = new GlobeView(
      null,
      this.editableGlobeName,
      this.appState.activeProject.id,
    )

    if (geoposition) {
      globeView.setGeoposition(geoposition, bounds)
    }

    const id = await this.saveGlobeView(globeView, filledImage)
    if (id) {
      this.selectGlobe(this.globeViewsStore.adoptSaved(globeView, id))
    }
  }

  public saveGlobeViewItems = async (
    items: IGlobeViewSpecificItemData[],
    { throwOnError = false }: { throwOnError?: boolean } = {},
  ): Promise<string | null> => {
    return new Promise<string | null>((resolve, reject) => {
      this.globeViewsStore.saveGlobeViewItems(
        items,
        this.selectedGlobeViewId,
        id => resolve(id),
        true,
        false,
        // Default: resolve null on failure so existing awaited callers
        // (PropertiesPanel, bulk saves) finish their finally/loader
        // teardown without emitting unhandled rejections. The underlying
        // saga still dispatches REQUEST_ERROR for Sentry + user toast, and
        // saveGlobeViewItems already rolled back the optimistic globe
        // mutation. Pending-placement opts in to throwOnError.
        error =>
          throwOnError
            ? reject(error ?? new Error('saveGlobeViewItems failed'))
            : resolve(null),
      )
    })
  }

  public deleteGlobeViewItem = async (itemId: string): Promise<string> => {
    return new Promise<string>(resolve => {
      this.globeViewsStore.deleteGlobeViewItem(
        itemId,
        id => resolve(id),
        true,
        false,
      )
    })
  }
}
